/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, createContext, useContext } from 'react';
import dynamic from 'next/dynamic';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import { lookupsByType, searchRestaurantsByLocation } from '@marriott/mi-awp-graphql';
import { useApolloClient } from '@apollo/client';
import Cookies from 'js-cookie';
import { StyledRecommendedOutlets, StyledVerticalSearchCards } from './RecommendedOutlets.styles';
import { Heading, Types, Text, Button } from '@marriott/mi-ui-library';
import {
  getBaiduCurrentLocation,
  getGoogleCurrentLocation,
  getSubDirectoryLocale,
  useGetBreakpoint,
  capitalizeFirstLetter,
} from '../../modules/utils/helpers';
import searchResultsMockData from '../../organisms/SearchResults/__mocks__/SearchResultsFiltersMock.json';
import { searchResultsState, useStore } from '../../modules/store/searchResultsStore';
import { PageParamsContext } from '../../modules/context';
import {
  bonvoyMemberType,
  COUNTRYTYPE,
  earnBonvoyPointsFilter,
  fullbleedClass,
  payBonvoyPointsFilter,
  simplifiedChineseLocale,
  AWPFILTERS,
  hongkongCityFacet,
  macauCityFacet,
  taipeiCityFacet,
  kaohsiungCityFacet,
} from '../../modules/utils/constants/constants';
import { CountryDetailsProps, SearchResultData } from './RecommendedOutlets.types';
import { TRACKING_CONSTANT } from '../../modules/utils/constants';
import { getDirection } from '../../modules/utils/helpers';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import countryDetailsInEnglish from './__mock__/countryDetailsInEnglish.json';

const {
  EARN_REDEEM_RECOMMENDATIONS,
  EARN_REDEEM_RECOMMENDATIONS_SEARCH_CTA,
  EARN_REDEEM_RECOMMENDATIONS_LOCATION_TRACKING_CTA,
  INTERNAL_LINK,
} = TRACKING_CONSTANT;

// Dynamic Imports
const SearchOutletCard = dynamic<unknown>(() =>
  import('../../organisms/SearchResults/SearchOutletCard').then(mod => mod.SearchOutletCard)
);

const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const RecommendedOutlets = (props: any) => {
  let countryCode = '';
  let lat = 0;
  let long = 0;
  let city = '';

  if (typeof window !== 'undefined' && window && window.dataLayer) {
    countryCode = window.dataLayer['browser_akamai_loc_country'] as string;
    lat = window.dataLayer['browser_akamai_loc_lat'] as number;
    long = window.dataLayer['browser_akamai_loc_long'] as number;
    city = window.dataLayer['browser_akamai_loc_city'] as string;
  }

  // check for HK, MO, TW location data from dataLayer
  const isHongKong = countryCode === 'HK';
  const isMacau = countryCode === 'MO';
  // "kaohsiung" and "taipei" are only two cities that have been seen from dataLayer
  const isTaiwan = countryCode === 'undefined' && (city === 'taipei' || city === 'kaohsiung');
  const cityDimensions = isHongKong
    ? [...hongkongCityFacet, city?.split(' ')?.map(capitalizeFirstLetter)?.join(' ')]
    : isMacau
    ? [...macauCityFacet, city?.split(' ')?.map(capitalizeFirstLetter)?.join(' ')]
    : isTaiwan
    ? city === 'taipei'
      ? [...taipeiCityFacet, city?.split(' ')?.map(capitalizeFirstLetter)?.join(' ')]
      : city === 'kaohsiung'
      ? [...kaohsiungCityFacet, city?.split(' ')?.map(capitalizeFirstLetter)?.join(' ')]
      : []
    : [];

  const [locationName, setLocationName] = useState('');
  const [locationNameEnglish, setLocationNameEnglish] = useState('');
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const setData = useStore((state: searchResultsState) => state.setData);
  const searchResultsData = useStore((state: searchResultsState) => state.searchResultData);
  const GOOGLE_MAP_API_KEY: any = useClientEnvVarsStore.getState().envVarsObject['GOOGLE_MAP_API_KEY'];
  const { currentLocale, headersData } = useContext(PageParamsContext);
  const subLocale = getSubDirectoryLocale(currentLocale || 'en-US');
  const isDirectionRTL = getDirection() === 'rtl';
  const isENUSLocale = currentLocale?.replace('_', '-')?.toLowerCase() === 'en-US'?.toLowerCase();

  const pageContext = useContext(createContext<any>({}));
  const requestId = useMemo(() => {
    return pageContext.requestId;
  }, [pageContext]);
  const sessionID = Cookies.get('sessionID');
  const currentTimestamp = Date.now();
  const currentDateTimeStamp = new Date(currentTimestamp).getTime();

  const countryDetailsClient = useApolloClient();
  async function getCountryDetails() {
    try {
      const { data } = await countryDetailsClient.query({
        query: lookupsByType,
        errorPolicy: 'all',
        variables: {
          type: COUNTRYTYPE,
        },
        context: {
          headers: {
            'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
            'x-request-id': requestId,
            'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
            'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
            'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
          },
        },
      });
      if (data) {
        const countryDetails = data?.lookupsByType?.lookups?.filter((country: CountryDetailsProps) => {
          if (isTaiwan) {
            return country.code === 'TW';
          }
          if (isHongKong || isMacau) {
            return country.code === 'CN';
          }

          return country.code === countryCode;
        });
        setLocationName(countryDetails[0]?.label);

        // set additional city facets

        const facetTerms = [
          {
            type: bonvoyMemberType,
            dimensions: [earnBonvoyPointsFilter, payBonvoyPointsFilter],
          },
          ...(isHongKong || isMacau || isTaiwan ? [{ type: AWPFILTERS.city, dimensions: cityDimensions }] : []),
        ];

        // set country code for searchByLocation query payload depending on dataLayer location data
        const isByCountrySearch = {
          country: countryDetails[0]?.code,
          facets: {
            terms: facetTerms,
            ranges: [],
          },
        };

        getSearchResultsDetails({
          variables: {
            limit: 6,
            search: isByCountrySearch,
          },
        });
      }
    } catch (error) {
      setLocationName('');
    }
  }

  const searchResultDetailsClient = useApolloClient();
  async function getSearchResultsDetails(inputValues: any) {
    try {
      const { data } = await searchResultDetailsClient.query({
        query: searchRestaurantsByLocation,
        errorPolicy: 'all',
        ...inputValues,
        context: {
          headers: {
            'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
            'x-request-id': requestId,
            'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
            'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
            'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
          },
        },
      });
      if (data && data?.searchRestaurantsByLocation?.edges?.length > 0) {
        setData(data, false, false, props?.dynamicMedia);
      }
    } catch (error) {
      setData([], false, true, []);
    }
  }

  const baiduMap = async () => {
    try {
      const response = await fetch('/services/marriott-hws/getBaiduMapsApiKey.json', {
        method: 'GET',
        headers: {
          'Content-Type': 'text/plain',
        },
      });
      if (response.status === 200) {
        const data = await response.json();
        const apiKey = atob(data?.apiKey);
        const script = document.createElement('script');
        script.src = `https://api.map.baidu.com/getscript?v=2.0&ak=${apiKey}&s=1`;
        script.id = 'baidu-script';
        script.defer = true;

        // Append the 'script' element to 'head'
        document.head.appendChild(script);
        script.addEventListener('load', function (e) {
          e.preventDefault();
          if (window?.BMap && window?.BMap.Map) {
            const event = new CustomEvent('BAIDU_SCRIPT_LOADED', { detail: { loaded: true } });
            document.dispatchEvent(event);
          }
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const googleMap = async () => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}`;
    script.id = 'google-map-script';
    script.defer = true;

    // Append the 'script' element to 'head'
    document.head.appendChild(script);
    script.addEventListener('load', function (e) {
      e.preventDefault();
      if (window?.google) {
        const event = new CustomEvent('GOOGLE_MAP_SCRIPT_LOADED', { detail: { loaded: true } });
        document.dispatchEvent(event);
      }
    });
  };

  useEffect(() => {
    if (!isENUSLocale) {
      const countryDetails = countryDetailsInEnglish?.filter(
        (country: CountryDetailsProps) => country.code === countryCode
      );
      setLocationNameEnglish(countryDetails[0]?.label);
    }
    getCountryDetails();
  }, []);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (props?.isAuthorMode) {
      setData(searchResultsMockData?.data, false, false, props?.dynamicMedia);
    }
  }, [props?.isAuthorMode]);

  useEffect(() => {
    if (currentLocale?.toLowerCase() === simplifiedChineseLocale.toLowerCase()) {
      baiduMap();
    } else googleMap();
  }, [currentLocale]);

  const makeGeoLocationCall = () => {
    const locationHandler = (data: Record<string, string>): void => {
      if (data) {
        const pageUrl = `https://${headersData['x-host']}${subLocale}/dining/search-results.mi${encodeURI(
          `?term=${data['address']}&lat=${lat}&long=${long}&bonvoyMembers=${props?.bonvoyPointsOffFolio},${props?.payWithBonvoyPoints}`
        )}`;

        const target = props?.showNearbyOpenNewTab ? '_blank' : '_self';
        window.open(pageUrl, target);
      }
    };
    if (currentLocale?.toLowerCase() === simplifiedChineseLocale.toLowerCase())
      getBaiduCurrentLocation(locationHandler);
    else getGoogleCurrentLocation(locationHandler);
  };

  return searchResultsData?.edges?.length > 0 ? (
    <StyledRecommendedOutlets
      data-component-name="o-awp-recommendationscardcontainer"
      data-testid="recommendationscardcontainer"
      className={(clsx('my-3'), props?.styleclass)}
    >
      <div
        className={clsx('container recommendations-container', {
          'px-md-0': props?.styleclass?.includes(fullbleedClass),
          'p-0': !props?.styleclass?.includes(fullbleedClass),
        })}
      >
        {(props?.title || props?.subTitle || props?.showNearbyLabel) && (
          <div
            className={clsx(
              'd-flex justify-content-between recommended-header-section',
              isMobileViewPort
                ? 'flex-column align-items-start'
                : props?.subTitle === ''
                ? 'flex-row align-items-center'
                : 'flex-row align-items-end'
            )}
          >
            <div className={clsx('recommended-header-section__title text-left')}>
              <Heading
                customClass="mb-2"
                titleText={props?.title}
                variation={Types.headingType?.title}
                fontSize={Types.size.small}
              />
              {props?.subTitle && (
                <Text
                  element={Types.tags.div}
                  fontSize={Types.size.medium}
                  copyText={props?.subTitle}
                  customClass={!isMobileViewPort ? 'my-1' : ''}
                />
              )}
            </div>
            {props?.showNearbyLabel && (
              <div
                className={clsx(
                  'recommended-header-section__location-link py-1',
                  isDirectionRTL ? 'text-right' : 'text-left'
                )}
              >
                <Button
                  className="m-link-tertiary-button show-venue-link"
                  custom_click_track_value={`${EARN_REDEEM_RECOMMENDATIONS}|${EARN_REDEEM_RECOMMENDATIONS_LOCATION_TRACKING_CTA}|${INTERNAL_LINK}`}
                  trackingProperties={props?.trackingProperties}
                  callback={makeGeoLocationCall}
                  isLink={true}
                >
                  {props?.showNearbyLabel}
                </Button>
              </div>
            )}
          </div>
        )}
        <StyledVerticalSearchCards className="my-3">
          {searchResultsData?.edges?.map((outlet: SearchResultData, index: number) => (
            <div className="recommended-cards" key={index}>
              <SearchOutletCard
                restaurant={outlet}
                {...props}
                isVerticalCard={true}
                showDescriptionAlways={true}
                currentLocale={subLocale}
              />
            </div>
          ))}
        </StyledVerticalSearchCards>
      </div>
      {props?.searchCtaLabelText && (
        <div className="d-flex justify-content-center">
          <Button
            className="m-button-s m-button-secondary my-3"
            target={props?.searchCtaOpenNewTab ? '_blank' : '_self'}
            isLink={true}
            href={
              isENUSLocale
                ? `${subLocale}/dining/search-results.mi` +
                  encodeURI(
                    `?term=${locationName}&term3=${
                      isTaiwan ? 'TW' : isHongKong || isMacau ? 'CN' : countryCode
                    }&bonvoyMembers=${props?.bonvoyPointsOffFolio},${
                      props?.payWithBonvoyPoints
                    }&countryDescription=${locationName}${
                      isHongKong || isMacau || isTaiwan ? `&city=${cityDimensions?.join(',')}` : ``
                    }`
                  )
                : `${subLocale}/dining/search-results.mi` +
                  encodeURI(
                    `?term=${locationName}&term3=${
                      isTaiwan ? 'TW' : isHongKong || isMacau ? 'CN' : countryCode
                    }&bonvoyMembers=${props?.bonvoyPointsOffFolio},${
                      props?.payWithBonvoyPoints
                    }&countryDescription=${locationName},${locationNameEnglish}${
                      isHongKong || isMacau || isTaiwan ? `&city=${cityDimensions?.join(',')}` : ``
                    }`
                  )
            }
            custom_click_track_value={`${EARN_REDEEM_RECOMMENDATIONS}|${EARN_REDEEM_RECOMMENDATIONS_SEARCH_CTA}|${INTERNAL_LINK}`}
            trackingProperties={props?.trackingProperties}
          >
            {props?.searchCtaLabelText}
          </Button>
        </div>
      )}
    </StyledRecommendedOutlets>
  ) : null;
};

export const RecommendedOutletsConfig = {
  emptyLabel: 'RecommendedOutlets',
  isEmpty: false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/recommendationscardcontainer`,
};

export const RecommendedOutletsEditable = (props: any) => (
  <EditableComponent config={RecommendedOutletsConfig} {...props}>
    <RecommendedOutlets {...props} />
  </EditableComponent>
);
